import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Modal, { ModalActions, ModalTitle, ModalBody } from '@strava/ui/Modal';
import Button from '@strava/ui/Button';
import Tooltip from '@strava/ui/Tooltip';
import NavigationHelpNormalXsmall from '@strava/icons/NavigationHelpNormalXsmall';
import I18n from 'utils/I18n';
import { Markup } from 'interweave';

import createNetworkingClient from 'utils/networking-client';
import { YOUNGEST_AGE_ON_PLATFORM } from 'constants/appConstants';
import styles from './index.scss';
import DisclaimerText from '../DisclaimerText';
import DropdownDatePicker from '../DropdownDatePicker';
import {
  LANDING_PAGE,
  MOBILE_REGISTRATION_PAGE,
  pageAttrForAnalytics,
  PRIVATE_ACTIVITY,
  REGISTRATION_PAGE,
  track
} from '../../../utils/onboarding';

const SignupAgeGateModal = ({
  open,
  onSuccessCallback,
  onDismissCallback,
  locale,
  page
}) => {
  const [dob, setDob] = useState({ month: '', day: '', year: '' });
  const [erroredControls, setErroredControls] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);

  const I18nPrefix = 'strava.landing_page';

  const copyCantMakeAccount = I18n.t(`${I18nPrefix}.cant_make_account`);

  useEffect(() => {
    if (open) {
      setErrorMessage(null);
      track({
        page: 'dob',
        action: 'screen_enter',
        properties: {
          source: pageAttrForAnalytics(page)
        }
      });
    }
  }, [open, page]);

  const isCompleteDate = ({ month, day, year }) => {
    let result = true;
    if (month === null || month === '') {
      setErroredControls((prev) => [...prev, 'month']);
      result = false;
    }
    if (day === null || day === '') {
      setErroredControls((prev) => [...prev, 'day']);
      result = false;
    }
    if (year === null || year === '') {
      setErroredControls((prev) => [...prev, 'year']);
      result = false;
    }
    return result;
  };

  const dateWithoutTime = (date) => {
    return new Date(date.toDateString());
  };

  /**
   * Athlete must be >= 13 years to create an account
   */
  const isAllowedAge = ({ month, day, year }) => {
    const inputDate = dateWithoutTime(new Date(year, month - 1, day)); // Jan => 1 for input UI
    const today = dateWithoutTime(new Date());
    const minDate = dateWithoutTime(
      new Date(
        today.getFullYear() - YOUNGEST_AGE_ON_PLATFORM,
        today.getMonth(),
        today.getDate()
      )
    );
    return inputDate <= minDate;
  };

  const remove = (arr, value) => {
    let i = 0;
    while (i < arr.length) {
      if (arr[i] === value) {
        arr.splice(i, 1);
      } else {
        i += 1;
      }
    }
    return arr;
  };

  const updateErrorState = ({ month, day, year }) => {
    if (month) {
      setErroredControls((prev) => remove(prev, 'month'));
    }
    if (day) {
      setErroredControls((prev) => remove(prev, 'day'));
    }
    if (year) {
      setErroredControls((prev) => remove(prev, 'year'));
    }
  };

  const handleSignup = () => {
    const { month, day, year } = dob;

    track({
      page: 'dob',
      action: 'click',
      element: 'agree_signup',
      properties: {
        source: pageAttrForAnalytics(page),
        dob: `${month === '' ? null : month}-${day === '' ? null : day}-${
          year === '' ? null : year
        }`, // eg. track as null-12-2020 instead of -12-2020
        dob_complete: isCompleteDate({ month, day, year }),
        dob_allowed: isCompleteDate({ month, day, year })
          ? isAllowedAge({ month, day, year })
          : false
      }
    });

    if (!isCompleteDate({ month, day, year })) {
      return; // block signup process
    }
    if (!isAllowedAge({ month, day, year })) {
      setErrorMessage(copyCantMakeAccount);
      return; // block signup process
    }
    createNetworkingClient()
      .post('/onboarding/age_gate', {
        age_gate_year: year,
        age_gate_month: month,
        age_gate_day: day
      })
      .then((response) => {
        if (response && response.status === 200) {
          onSuccessCallback();
        } else {
          setErrorMessage(I18n.t(`${I18nPrefix}.errors.generic`));
        }
      })
      .catch(() => {
        setErrorMessage(I18n.t(`${I18nPrefix}.errors.generic`));
      });
  };

  const handleDobChange = (d) => {
    setDob(d);
    updateErrorState(d);
    setErrorMessage(null);
  };

  return (
    <Modal
      className={styles.modal}
      isOpen={open}
      onDismiss={onDismissCallback}
      hasClose={true}
      aria-labelledby="sign-up-modal-title"
    >
      <ModalTitle className={styles.modalTitle} id="sign-up-modal-title">
        {I18n.t(`${I18nPrefix}.before_you_sign_up`)}
      </ModalTitle>
      <ModalBody className={styles.modalBody}>
        <div className={styles.subtitle}>
          <div className={styles.text}>{I18n.t(`${I18nPrefix}.birthday`)}</div>
          <Tooltip
            label={
              <Markup noWrap={true} content={I18n.t(`${I18nPrefix}.why_dob`)} />
            }
            position="top"
            className={styles.toolTip}
          >
            <NavigationHelpNormalXsmall />
          </Tooltip>
        </div>

        <DropdownDatePicker
          className={styles.datepicker}
          dateChangeCallback={handleDobChange}
          erroredControls={erroredControls}
          locale={locale}
          dateMax={new Date(new Date().toDateString())} // today - we don't restrict options during account creation flow
          enableDefaultFocus={true}
        />

        <DisclaimerText showLoginOption={false} />

        {errorMessage && (
          <div
            data-testid="sign-up-modal-error-message"
            className={styles.errorMessage}
          >
            {errorMessage}
          </div>
        )}
      </ModalBody>
      <ModalActions className={styles.modalActions}>
        <Button
          type="button"
          variant="primary"
          className={styles.signupButton}
          onClick={handleSignup}
          data-testid="sign-up-age-gate-submit"
        >
          {I18n.t(`${I18nPrefix}.agree_and_signup`)}
        </Button>
      </ModalActions>
    </Modal>
  );
};

SignupAgeGateModal.defaultProps = {
  page: ''
};

SignupAgeGateModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onSuccessCallback: PropTypes.func.isRequired,
  onDismissCallback: PropTypes.func.isRequired,
  locale: PropTypes.string.isRequired,
  page: PropTypes.oneOf([
    LANDING_PAGE,
    PRIVATE_ACTIVITY,
    MOBILE_REGISTRATION_PAGE,
    REGISTRATION_PAGE
  ])
};

export default SignupAgeGateModal;
